import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Search, Archive, ArrowLeft, X } from "lucide-react";
import { LottoCard, LottoActionModal, ArchiveConfirmDialog } from "../components/lotti";
import { LottoPositionForm } from "../components/lotti/LottoPositionDialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ArchivioLotti = () => {
  const { getAuthHeaders, localeCorrente } = useAuth();
  const navigate = useNavigate();

  const [lotti, setLotti] = useState([]);
  const [ingredienti, setIngredienti] = useState([]);
  const [frigoriferi, setFrigoriferi] = useState([]);
  const [posizioni, setPosizioni] = useState([]);
  const requestRef = useRef(0);
  const loadedLocaleRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [selectedLotto, setSelectedLotto] = useState(null);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [lottoToArchive, setLottoToArchive] = useState(null);
  const [positionOnlyLotto, setPositionOnlyLotto] = useState(null);
  const [positionOnlyValue, setPositionOnlyValue] = useState("");
  const [positionOnlySaving, setPositionOnlySaving] = useState(false);

  const fetchData = async () => {
    if (!localeCorrente) {
      requestRef.current += 1;
      loadedLocaleRef.current = null;
      setLotti([]);
      setFrigoriferi([]);
      setLoading(false);
      return;
    }
    const requestId = ++requestRef.current;
    const localeId = localeCorrente.id;
    if (loadedLocaleRef.current !== localeId) {
      setLotti([]);
      setIngredienti([]);
      setPosizioni([]);
      setFrigoriferi([]);
      setLoading(true);
    }
    try {
      const headers = getAuthHeaders();
      const [lottiRes, ingRes, frigoRes, posRes] = await Promise.all([
        axios.get(`${API}/lotti?locale_id=${localeId}&archived=true`, { headers }),
        axios.get(`${API}/ingredienti?locale_id=${localeId}`, { headers }),
        axios.get(`${API}/frigoriferi?locale_id=${localeId}`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/posizioni?locale_id=${localeId}`, { headers }).catch(() => ({ data: [] })),
      ]);
      if (requestId !== requestRef.current) return;
      setLotti(lottiRes.data);
      setIngredienti(ingRes.data);
      setFrigoriferi(Array.isArray(frigoRes.data) ? frigoRes.data.filter(f => f.locale_id === localeId) : []);
      setPosizioni(Array.isArray(posRes.data) ? posRes.data : []);
      loadedLocaleRef.current = localeId;
    } catch (err) {
      console.error("Archivio fetch error:", err);
      toast.error("Errore caricamento archivio");
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    setActionModalOpen(false);
    setSelectedLotto(null);
    setPositionOnlyLotto(null);
    fetchData();
  }, [localeCorrente?.id]); // eslint-disable-line

  const getProdottoNome = (lotto) => {
    if (lotto.is_composto && lotto.nome_preparazione) {
      return lotto.nome_preparazione;
    }
    if (lotto.nome_prodotto) {
      return lotto.nome_prodotto;
    }
    if (lotto.ingrediente_id) {
      const ing = ingredienti.find(i => i.id === lotto.ingrediente_id);
      if (ing) return ing.nome;
    }
    return lotto.lotto_codice || "Senza nome";
  };

  const filtered = lotti.filter(l => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (l.nome_prodotto || "").toLowerCase().includes(q) ||
      (l.lotto_codice || "").toLowerCase().includes(q) ||
      getProdottoNome(l).toLowerCase().includes(q)
    );
  });

  const handleRestore = async (lottoId) => {
    try {
      await axios.put(`${API}/lotti/${lottoId}/restore`, {}, { headers: getAuthHeaders() });
      toast.success("Lotto ripristinato");
      setActionModalOpen(false);
      fetchData();
    } catch {
      toast.error("Errore ripristino");
    }
  };

  const handlePermanentDelete = async (lottoId) => {
    try {
      await axios.delete(`${API}/lotti/${lottoId}`, { headers: getAuthHeaders() });
      toast.success("Lotto eliminato definitivamente");
      setActionModalOpen(false);
      setArchiveDialogOpen(false);
      fetchData();
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  const openDetailModal = (lotto) => {
    toast.dismiss();
    setSelectedLotto(lotto);
    setActionModalOpen(true);
  };
  const getNomePosizione = id => {
    if (!id) return "";
    return frigoriferi.find(f => f.id === id)?.nome ||
      posizioni.find(p => p.id === id)?.nome || "Posizione precedente non disponibile";
  };

  const handlePositionOnlySave = async () => {
    if (!positionOnlyLotto || positionOnlySaving) return;
    setPositionOnlySaving(true);
    try {
      await axios.patch(`${API}/lotti/${positionOnlyLotto.id}`, { posizione: positionOnlyValue || "" },
        { headers: getAuthHeaders() });
      setActionModalOpen(false);
      setSelectedLotto(null);
      setPositionOnlyLotto(null);
      setLotti(current => current.map(item => item.id === positionOnlyLotto.id
        ? { ...item, posizione: positionOnlyValue || "" } : item));
      toast.success("Posizione aggiornata");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossibile aggiornare la posizione");
    } finally {
      setPositionOnlySaving(false);
    }
  };

  const handlePrint = async (lotto) => {
    toast.loading("Generazione PDF etichetta...", { id: "archive-print" });
    try {
      const response = await axios.post(`${API}/lotti/stampa-pdf`, {
        lotto_ids: [lotto.id], width_mm: 50, height_mm: 30, rotated: false,
      }, { headers: getAuthHeaders(), responseType: "blob" });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const filename = `etichetta_${lotto.id}.pdf`;
      const file = new File([blob], filename, { type: "application/pdf" });
      if (navigator.canShare?.({ files: [file] }) && navigator.share) {
        try {
          await navigator.share({ files: [file], title: "Etichetta Yollo" });
          toast.success("Etichetta pronta per la stampa", { id: "archive-print" });
          return;
        } catch (error) {
          if (error.name === "AbortError") { toast.dismiss("archive-print"); return; }
        }
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success("PDF scaricato — aprilo per stamparlo", { id: "archive-print" });
    } catch (error) {
      console.error("Stampa etichetta archivio:", error);
      toast.error("Impossibile generare l'etichetta", { id: "archive-print" });
    }
  };

  return (
    <div className="page-container pb-12" data-testid="archivio-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate("/etichette")}
          className="flex items-center justify-center w-10 h-10 rounded-xl bg-muted border border-border text-muted-foreground"
          data-testid="back-to-lotti"
          aria-label="Torna ai lotti"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex-1">
          <h1 className="font-extrabold text-xl tracking-tight flex items-center gap-2">
            <Archive className="h-5 w-5 text-muted-foreground" />
            Archivio
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5" data-testid="archivio-count">{lotti.length} lotti archiviati</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Cerca nell'archivio..."
          className="pl-10 h-11 bg-card border-border text-foreground placeholder:text-muted-foreground rounded-xl"
          data-testid="search-archivio"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery("")}
            data-testid="archivio-clear-search-btn"
            className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-muted rounded-full"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        )}
      </div>

      {/* Lista */}
      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-card rounded-xl animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 px-4 bg-card rounded-2xl border border-border">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
            <Archive className="h-8 w-8 text-muted-foreground" />
          </div>
          <p className="text-foreground font-bold text-base mb-1">
            {searchQuery ? "Nessun risultato" : "Archivio vuoto"}
          </p>
          <p className="text-sm text-muted-foreground text-center max-w-xs">
            {searchQuery ? "Modifica la ricerca per trovare i lotti archiviati." : "I lotti archiviati appariranno qui."}
          </p>
        </div>
      ) : (
        <div className="space-y-2 md:grid md:grid-cols-2 md:gap-3 md:space-y-0 lg:grid-cols-3" data-testid="archivio-list">
          {filtered.map((item) => (
            <LottoCard
              key={item.id}
              lotto={item}
              prodottoNome={getProdottoNome(item)}
              posizioneNome={getNomePosizione(item.posizione)}
              isSelected={false}
              onClick={() => openDetailModal(item)}
              isArchived={true}
            />
          ))}
        </div>
      )}

      {/* Modali */}
      <LottoActionModal
        open={actionModalOpen}
        onClose={() => { if (!positionOnlySaving) { setActionModalOpen(false); setSelectedLotto(null); setPositionOnlyLotto(null); } }}
        lotto={selectedLotto}
        prodottoNome={selectedLotto ? getProdottoNome(selectedLotto) : ""}
        onPrint={handlePrint}
        onEdit={lotto => { setPositionOnlyValue(lotto.posizione || ""); setPositionOnlyLotto(lotto); }}
        onArchive={(l) => { setLottoToArchive(l); setArchiveDialogOpen(true); setActionModalOpen(false); }}
        onRestore={handleRestore}
        onDelete={handlePermanentDelete}
        isArchived={true}
        archiveEditView={positionOnlyLotto ? (
          <div className="p-4 sm:p-6 space-y-4" data-testid="lotto-posizione-dialog">
            <LottoPositionForm lotto={positionOnlyLotto} prodottoNome={getProdottoNome(positionOnlyLotto)}
              value={positionOnlyValue} onChange={setPositionOnlyValue}
              onClose={() => setPositionOnlyLotto(null)} onSave={handlePositionOnlySave}
              saving={positionOnlySaving} frigoriferi={frigoriferi} posizioni={posizioni} />
          </div>
        ) : null}
      />

      <ArchiveConfirmDialog
        open={archiveDialogOpen}
        onOpenChange={setArchiveDialogOpen}
        onArchive={() => {}}
        onDelete={() => lottoToArchive && handlePermanentDelete(lottoToArchive.id)}
        lottoName={lottoToArchive ? getProdottoNome(lottoToArchive) : "questo lotto"}
        isAlreadyArchived={true}
      />
    </div>
  );
};

export default ArchivioLotti;
